import { NavLink, Outlet } from "react-router-dom";
import { CalendarDays, Camera, FileText, Home, Images, Leaf, Link2, LogOut, Palette, PenTool, Pin, Smartphone, Workflow } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Heart, Star } from "@/components/Doodles";
import { BRAND } from "@/lib/site";

const NAV = [
  { to: "/admin", label: "Today", icon: CalendarDays, id: "nav-home" },
  { to: "/admin/listings", label: "Listings", icon: Home, id: "nav-dashboard" },
  { to: "/admin/studio", label: "Studio", icon: Palette, id: "nav-studio" },
  { to: "/admin/review", label: "Review", icon: Smartphone, id: "nav-review" },
  { to: "/admin/automation", label: "Autopilot", icon: Workflow, id: "nav-automation" },
  { to: "/admin/articles", label: "Articles", icon: FileText, id: "nav-articles" },
  { to: "/admin/coloring", label: "Coloring", icon: PenTool, id: "nav-coloring" },
  { to: "/admin/images", label: "Images", icon: Images, id: "nav-images" },
  { to: "/admin/pins", label: "Pins", icon: Pin, id: "nav-pins" },
  { to: "/admin/seasons", label: "Seasons", icon: Leaf, id: "nav-seasons" },
  { to: "/admin/photos", label: "Photos", icon: Camera, id: "nav-photos" },
  { to: "/admin/affiliate", label: "Affiliate", icon: Link2, id: "nav-affiliate" },
];

const linkClass = ({ isActive }: { isActive: boolean }) =>
  `flex items-center gap-3 rounded-2xl px-4 py-3 font-display font-extrabold transition-colors duration-200 ${
    isActive ? "bg-[#F6D776] text-[#23324A] shadow-[3px_3px_0_#23324A] border-2 border-[#23324A]" : "text-[#23324A]/75 hover:bg-white border-2 border-transparent"
  }`;

export default function AppShell() {
  const { user, logout } = useAuth();
  return (
    <div className="min-h-screen md:flex">
      <aside className="sticky top-0 hidden h-screen w-64 shrink-0 flex-col border-r-2 border-dashed border-[#23324A]/25 bg-[#FDFBF7] px-5 py-8 md:flex">
        <div className="relative mb-10" data-testid="brand-logo">
          <Star className="absolute -left-2 -top-4 h-7 w-7 rotate-12" />
          <p className="pl-6 font-display text-2xl font-black leading-none text-[#23324A]">{BRAND}</p>
          <p className="mt-1 pl-6 text-xs font-semibold text-[#23324A]/60">Printables studio</p>
        </div>
        <nav className="flex flex-col gap-2">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.to === "/admin"} className={linkClass} data-testid={n.id}>
              <n.icon className="h-5 w-5" strokeWidth={2.5} /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="mt-auto">
          <Heart className="mb-3 h-8 w-8 -rotate-6" />
          <p className="truncate text-xs text-[#23324A]/60" data-testid="sidebar-user-email">{user ? user.email : ""}</p>
          <button onClick={logout} data-testid="logout-btn" className="mt-2 flex items-center gap-2 text-sm font-bold text-[#23324A] hover:text-[#F08A6C]">
            <LogOut className="h-4 w-4" strokeWidth={2.5} /> Log out
          </button>
        </div>
      </aside>

      <div className="flex items-center justify-between border-b-2 border-dashed border-[#23324A]/25 px-5 py-4 md:hidden">
        <p className="font-display text-xl font-black text-[#23324A]">{BRAND}</p>
        <button onClick={logout} data-testid="mobile-logout-btn" aria-label="Log out"><LogOut className="h-5 w-5 text-[#23324A]" strokeWidth={2.5} /></button>
      </div>

      <main className="min-w-0 flex-1 px-5 pb-28 pt-6 sm:px-8 md:px-12 md:pb-12 md:pt-10">
        <Outlet />
      </main>

      <nav className="fixed inset-x-0 bottom-0 z-40 flex gap-1 overflow-x-auto border-t-2 border-[#23324A] bg-[#FDFBF7] px-2 py-2 md:hidden">
        {NAV.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.to === "/admin"} data-testid={`mobile-${n.id}`}
            className={({ isActive }) => `flex min-w-[64px] shrink-0 flex-col items-center rounded-xl px-2 py-1.5 text-xs font-bold ${isActive ? "bg-[#F6D776] text-[#23324A]" : "text-[#23324A]/70"}`}>
            <n.icon className="h-5 w-5" strokeWidth={2.5} />
            {n.label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
